var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { LitElement, html, css, nothing } from 'lit';
import { property } from 'lit/decorators.js';
import { classMap } from 'lit/directives/class-map.js';
/**
 * A customizable alert component for displaying important information to users.
 *
 * @slot - The alert message content
 * @fires dismiss - Fired when the dismiss button is clicked
 */
export class KRAlert extends LitElement {
    constructor() {
        super(...arguments);
        /**
         * The alert type/severity
         */
        this.type = 'info';
        /**
         * Optional heading text shown above the message.
         */
        this.label = '';
        /**
         * @deprecated Use `label`. Kept so existing `title="..."` usages keep rendering a heading, but
         * `title` is also the native HTML attribute, so the browser shows it as a hover tooltip too.
         * `label` wins when both are set.
         */
        this.title = '';
        /**
         * Whether the alert can be dismissed
         */
        this.dismissible = false;
        /**
         * Whether the alert is visible
         */
        this.visible = true;
    }
    /** Handles dismiss button click */
    _handleDismiss() {
        this.visible = false;
        this.dispatchEvent(new CustomEvent('dismiss', {
            bubbles: true,
            composed: true
        }));
    }
    render() {
        const icons = {
            info: html `
        <svg
          class="icon"
          viewBox="0 0 20 20"
          fill="currentColor"
        >
          <path
            fill-rule="evenodd"
            d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
            clip-rule="evenodd"
          />
        </svg>
      `,
            success: html `
        <svg
          class="icon"
          viewBox="0 0 20 20"
          fill="currentColor"
        >
          <path
            fill-rule="evenodd"
            d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
            clip-rule="evenodd"
          />
        </svg>
      `,
            warning: html `
        <svg
          class="icon"
          viewBox="0 0 20 20"
          fill="currentColor"
        >
          <path
            fill-rule="evenodd"
            d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
            clip-rule="evenodd"
          />
        </svg>
      `,
            error: html `
        <svg
          class="icon"
          viewBox="0 0 20 20"
          fill="currentColor"
        >
          <path
            fill-rule="evenodd"
            d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
            clip-rule="evenodd"
          />
        </svg>
      `,
        };
        return html `
      <div
        class=${classMap({
            'alert': true,
            ['alert--' + this.type]: true,
            'alert--has-header': !!(this.label || this.title),
            'alert--hidden': !this.visible
        })}
        role="alert"
      >
        ${icons[this.type]}
        <div class="content">
          ${(this.label || this.title)
            ? html `<h4 class="header">${this.label || this.title}</h4>`
            : nothing}
          <div class="message">
            <slot></slot>
          </div>
        </div>
        ${this.dismissible
            ? html `
            <button
              class="dismiss"
              type="button"
              aria-label="Dismiss alert"
              @click=${this._handleDismiss}
            >
              <svg
                viewBox="0 0 20 20"
                fill="currentColor"
                width="16"
                height="16"
              >
                <path
                  fill-rule="evenodd"
                  d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                  clip-rule="evenodd"
                />
              </svg>
            </button>
          `
            : nothing}
      </div>
    `;
    }
}
KRAlert.styles = css `
    :host {
      display: block;
    }

    .alert {
      display: flex;
      align-items: center;
      gap: 12px;
      padding: 12px 16px;
      border-radius: 8px;
      border: 1px solid;
      border-left: 4px solid;
    }

    .alert--has-header {
      align-items: flex-start;
    }

    .alert--has-header .icon {
      margin-top: 2px;
    }

    .alert--hidden {
      display: none;
    }
      
    /* Variants */
    .alert--info {
      background-color: #eff6ff;
      border-color: #3b82f6;
    }

    .alert--info .icon {
      color: #2563eb;
    }

    .alert--success {
      background-color: #f0fdf4;
      border-color: #22c55e;
    }

    .alert--success .icon {
      color: #16a34a;
    }

    .alert--warning {
      background-color: #fffbeb;
      border-color: #f59e0b;
    }

    .alert--warning .icon {
      color: #d97706;
    }

    .alert--error {
      background-color: #fef2f2;
      border-color: #ef4444;
    }

    .alert--error .icon {
      color: #dc2626;
    }

    .icon {
      flex-shrink: 0;
      width: 20px;
      height: 20px;
    }

    .content {
      flex: 1;
      min-width: 0;
      display: flex;
      flex-direction: column;
      gap: 6px;
    }

    .header {
      font-size: 14px;
      font-weight: 600;
      color: #000000;
      margin: 0;
    }

    .message {
      font-size: 14px;
      color: #000000;
      margin: 0;
    }

    .dismiss {
      flex-shrink: 0;
      display: flex;
      align-items: center;
      justify-content: center;
      width: 20px;
      height: 20px;
      padding: 0;
      background: none;
      border: none;
      border-radius: 4px;
      color: #6b7280;
      cursor: pointer;
      transition: background 0.15s ease, color 0.15s ease;
    }

    .dismiss:hover {
      background-color: rgba(0, 0, 0, 0.05);
      color: #374151;
    }

    .dismiss:focus-visible {
      outline: 2px solid #3b82f6;
      outline-offset: 2px;
    }
  `;
__decorate([
    property({ type: String })
], KRAlert.prototype, "type", void 0);
__decorate([
    property({ type: String })
], KRAlert.prototype, "label", void 0);
__decorate([
    property({ type: String })
], KRAlert.prototype, "title", void 0);
__decorate([
    property({ type: Boolean })
], KRAlert.prototype, "dismissible", void 0);
__decorate([
    property({ type: Boolean })
], KRAlert.prototype, "visible", void 0);
// Guarded registration: no-op if the tag is already defined, so a second copy of krubble on
// the page (e.g. the Operations shell loads krubble globally while an embedded app bundled its
// own copy) doesn't throw. First definition wins.
if (!customElements.get('kr-alert')) {
    customElements.define('kr-alert', KRAlert);
}
//# sourceMappingURL=alert.js.map